import React, { useMemo } from 'react'
import NumberedStepLabel from 'core/components/validatedForm/NumberedStepLabel'
import CodeBlock from 'core/components/CodeBlock'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import Text from 'core/elements/Text'
import CopyToClipboard from 'core/components/CopyToClipboard'
import { sessionStoreKey, SessionState } from 'core/session/sessionReducers'
import { prop } from 'ramda'
import { useSelector } from 'react-redux'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { listUserTenants } from 'account/components/userManagement/tenants/new-actions'
import useListAction from 'core/hooks/useListAction'
import { userTenantsSelector } from 'account/components/userManagement/tenants/selectors'
import { getDomainName } from 'api-client/helpers'
import DocumentMeta from 'core/components/DocumentMeta'
import Modal from 'core/elements/modal'
import Button from 'core/elements/button'
import { getInfraRegionURL } from 'k8s/components/kaapi/secrets/actions'
import ModalInfo from 'core/elements/modal/ModalInfo'
import SimpleLink from 'core/components/SimpleLink'

export default function AddBYOHHosts({ onClose }) {
  const classes = useStyles()
  const selectSessionState = prop<string, SessionState>(sessionStoreKey)
  const session = useSelector(selectSessionState)
  const activeRegionName = session?.activeRegion
  const { dex_client_secret } = session
  const { getUserPrefs } = useScopedPreferences()
  const { currentTenant, currentTenantName } = getUserPrefs(session?.username)
  const fqdn = new URL(getInfraRegionURL()).hostname

  useListAction(listUserTenants)
  const tenants = useSelector(userTenantsSelector)
  const tenant = useMemo(() => tenants.find((tenant) => tenant.id === currentTenant), [tenants])
  const domainName = getDomainName()

  return (
    <Modal
      open={true}
      title={`Add New Physical Host`}
      onClose={onClose}
      panel="drawer"
      maxWidth={780}
      slideFrom="right"
      onBackdropClick={onClose}
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
      footerButtonAlignment="end"
    >
      <DocumentMeta title="Add a New Physical Host" />
      <div className={classes.root}>
        <ModalInfo
          icon="server"
          text={
            <>
              The host agent package is personalized for your account. Do not share it with anyone
              outside your organization. Once the host agent is installed, you will see the host in
              your list of{' '}
              <SimpleLink onClick={onClose}>
                <b>Physical Hosts</b>
              </SimpleLink>
              .
            </>
          }
        />
        <div className={classes.section}>
          <Text variant="subtitle2">Adding a New Physical Host</Text>
          <div className={classes.tutorial}>
            <NumberedStepLabel
              step={1}
              title="Login to your host terminal"
              className={classes.tutorialStep}
            />
            <NumberedStepLabel
              step={2}
              title="Install the host agent by invoking below commands as root user:"
              className={classes.tutorialStep}
            />
            <div className={classes.codeBlocks}>
              <CopyToClipboard
                copyText={`bash <(curl -s https://byohctl.s3.us-west-2.amazonaws.com/byohctl-setup)`}
              >
                <CodeBlock>
                  <Text variant="body2">
                    {`bash <(curl -s https://byohctl.s3.us-west-2.amazonaws.com/byohctl-setup)`}
                  </Text>
                </CodeBlock>
              </CopyToClipboard>
              <CopyToClipboard
                copyText={`byohctl onboard -u ${fqdn} -e ${session?.username} -d ${domainName} -c ${dex_client_secret} -t ${currentTenantName} -r ${activeRegionName} -p <password>`}
              >
                <CodeBlock>
                  <Text variant="body2">
                    {`byohctl onboard -u ${fqdn} -e ${session?.username} -d ${domainName} -c ${dex_client_secret} -t ${currentTenantName} -r ${activeRegionName} -p <password>`}
                  </Text>
                </CodeBlock>
              </CopyToClipboard>
              <Text variant="body2">
                <b>NOTE:{'  '}</b>
                {`Replace the <password> with your password`}
              </Text>
            </div>
          </div>
        </div>
        <div className={classes.section}>
          <Text variant="subtitle2">Here is your info for quick use:</Text>
          <div className={classes.credentials}>
            <Text component="div" variant="body2">
              Account URL:{' '}
            </Text>
            <CopyToClipboard copyText={fqdn}>
              <CodeBlock>
                <Text variant="body2" component="span">
                  {fqdn}
                </Text>
              </CodeBlock>
            </CopyToClipboard>
            <Text component="div" variant="body2">
              Username:{' '}
            </Text>
            <CopyToClipboard copyText={session?.username}>
              <CodeBlock>
                <Text variant="body2" component="span">
                  {session?.username}
                </Text>
              </CodeBlock>
            </CopyToClipboard>
            <Text component="div" variant="body2">
              Domain:{' '}
            </Text>
            <CopyToClipboard copyText={domainName}>
              <CodeBlock>
                <Text variant="body2" component="span">
                  {domainName}
                </Text>
              </CodeBlock>
            </CopyToClipboard>
            <Text component="div" variant="body2">
              Tenant:{' '}
            </Text>
            <CopyToClipboard copyText={tenant?.name}>
              <CodeBlock>
                <Text variant="body2" component="span">
                  {tenant?.name}
                </Text>
              </CodeBlock>
            </CopyToClipboard>
            <Text component="div" variant="body2">
              Region:{' '}
            </Text>
            <CopyToClipboard copyText={activeRegionName as string}>
              <CodeBlock>
                <Text variant="body2" component="span">
                  {activeRegionName as string}
                </Text>
              </CodeBlock>
            </CopyToClipboard>
            <Text component="div" variant="body2">
              client-token:{' '}
            </Text>
            <CopyToClipboard copyText={dex_client_secret}>
              <CodeBlock>
                <Text variant="body2" component="span">
                  {dex_client_secret}
                </Text>
              </CodeBlock>
            </CopyToClipboard>
          </div>
        </div>
      </div>
    </Modal>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  tutorial: {
    display: 'grid',
    gridGap: theme.spacing(1),
  },
  tutorialStep: {
    '& .title': {
      ...theme.typography.body2,
    },
  },
  codeBlocks: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    gap: theme.spacing(1),
    paddingLeft: theme.spacing(5),
  },
  root: {
    display: 'grid',
    gap: theme.spacing(3),
  },
  section: {
    display: 'grid',
    gap: theme.spacing(2),
  },
  credentials: {
    display: 'grid',
    gap: theme.spacing(1),
    gridTemplateColumns: '1fr 5fr',
    alignItems: 'center',
  },
}))
